import { Header } from '@almbrand/header';
import { useDeviceQuery } from '@almbrand/usedevicequery';
import { ThemeNames } from 'constantsValues/themeNames';
import { PageContext } from 'context/PageContext';
import { useContext } from 'react';
import { NavLink } from 'react-router-dom';

export const HeaderComponent: React.FC<HeaderReference> = ({
	logoImage,
	logoUrl,
	headerLinks,
	loginUrl,
	loginLabelText,
	//	menuMobilIcon,
	//	menuCloseMobilIcon,
	personalizedLinks,
	claimsLabelText,
	claimsUrl,
}) => {
	const rootUrl = window.location.origin;
	const { isMobile } = useDeviceQuery();
	const pageContext = useContext(PageContext);
	const theme = pageContext.theme;

	// Get icon props based on theme
	const getIconProps = () => {
		if (!isMobile) return {};

		const iconSize = theme === ThemeNames.almbrand ? '16px' : '24px';

		return {
			iconProps: {
				themeName: theme,
				icon: 'lock_closed' as const,
				width: iconSize,
				height: iconSize,
			},
		};
	};

	const mapHeaderLinks = (links: any[]) => {
		return links
			.map((link) => ({
				text: link?.text ?? '',
				title: link?.title ?? '',
				url: link?.url ?? '',
				target: link?.target ?? '',
				isExternalLink: link?.isExternalLink ?? false,
				useVueRoute: link?.useVueRoute ?? false,
				trackEvent: JSON.stringify({
					event: 'trackNavigation',
					navigationAction: link?.text ?? '',
					navigationDestination: `${rootUrl}${link?.url ?? ''}`,
					navigationType: 'primary navigation',
				}),
			}))
			.filter((link) => link.text && link.url);
	};

	const segmentedHeaderLinks = personalizedLinks?.items?.map((item) => ({
		text: item.content.urlLabel ?? '',
		url: item.content.url?.url ?? '',
		title: item.content.urlLabel ?? '',
		target: '_blank',
		isExternalLink: false,
		useVueRoute: false,
		trackEvent: JSON.stringify({
			event: 'trackNavigation',
			navigationAction: item.content.urlLabel ?? '',
			navigationDestination: item.content.url?.url ?? '',
			navigationType: 'segment navigation',
		}),
	}));

	return (
		<Header
			logoProps={{
				logoUrl: logoImage?.url ?? '',
				link: logoUrl?.url ?? '',
				...(theme === ThemeNames.almbrand ? { width: isMobile ? '44px' : '88px' } : {}),
				trackingEvent: {
					event: 'trackNavigation',
					navigationAction: 'Logo',
					navigationDestination: `${rootUrl}${logoUrl?.url ?? ''}`,
					navigationType: 'primary navigation',
				},
			}}
			navigationLinks={mapHeaderLinks(headerLinks)}
			segmentLinks={segmentedHeaderLinks}
			supportingButtonProps={{
				buttonType: isMobile ? 'link' : 'circle',
				children: claimsLabelText,
				size: 'small',
				linkButtonSettings: {
					href: claimsUrl?.url,
				},
				...(!isMobile && {
					iconProps: {
						themeName: theme,
						icon: 'user' as const,
						width: '40px',
						height: '40px',
					},
				}),
				trackingEvent: {
					event: 'trackNavigation',
					navigationAction: claimsLabelText ?? '',
					navigationDestination: `${rootUrl}${claimsUrl?.url ?? ''}`,
					navigationType: 'primary navigation',
				},
			}}
			buttonProps={{
				buttonType: 'personal',
				children: loginLabelText,
				size: 'small',
				linkButtonSettings: {
					href: loginUrl?.url,
				},
				...getIconProps(),
				iconPosition: 'left',
				trackingEvent: {
					event: 'trackNavigation',
					navigationAction: loginLabelText ?? '',
					navigationDestination: `${rootUrl}${loginUrl?.url ?? ''}`,
					navigationType: 'primary navigation',
				},
			}}
			renderChildren={(link: Link): JSX.Element => {
				return (
					<NavLink
						key={link.url}
						to={link.url}
						title={link.title}
						target={link.target}
						data-track-event={link.trackEvent}
					>
						{link.text}
					</NavLink>
				);
			}}
		/>
	);
};
